import React, { useState, useEffect } from 'react';
import { RadioButtonGroup, RadioButton } from 'carbon-components-react';
import './styles.scss';

export enum GeoscapeApproach {
  ORIGINAL = 'original',
  REPLACE = 'replace',
  FALLBACK = 'fallback'
}

export enum WidgetPosition {
  TOP_RIGHT = 'top-right',
  TOP_LEFT = 'top-left',
  BOTTOM_RIGHT = 'bottom-right',
  BOTTOM_LEFT = 'bottom-left'
}

interface Props {
  visible: boolean;
  position?: WidgetPosition;
  onApproachChange: (approach: GeoscapeApproach) => void;
}

const STORAGE_KEY = 'geoscape-test-approach';

export const GeoscapeTestWidget: React.FC<Props> = ({
  visible,
  position = WidgetPosition.BOTTOM_RIGHT,
  onApproachChange
}) => {
  const getInitialApproach = (): GeoscapeApproach => {
    const stored = localStorage.getItem(STORAGE_KEY);
    switch (stored) {
      case GeoscapeApproach.ORIGINAL:
        return GeoscapeApproach.ORIGINAL;
      case GeoscapeApproach.REPLACE:
        return GeoscapeApproach.REPLACE;
      case GeoscapeApproach.FALLBACK:
        return GeoscapeApproach.FALLBACK;
      default:
        return GeoscapeApproach.REPLACE;
    }
  };

  const [selectedApproach, setSelectedApproach] = useState<GeoscapeApproach>(
    getInitialApproach()
  );

  useEffect(() => {
    // Initialize the approach when component mounts
    onApproachChange(selectedApproach);
  }, [onApproachChange, selectedApproach]);

  const handleApproachChange = (approach: GeoscapeApproach) => {
    setSelectedApproach(approach);
    localStorage.setItem(STORAGE_KEY, approach);
    onApproachChange(approach);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className={`geoscape-test-widget geoscape-test-widget--${position}`}>
      <div className="geoscape-test-widget__header">
        <h4>
          <span role="img" aria-label="QA Geoscape Testing">
            🧪
          </span>{' '}
          QA Geoscape Testing
        </h4>
        <span className="geoscape-test-widget__badge">QA Only</span>
      </div>

      <div className="geoscape-test-widget__content">
        <p className="geoscape-test-widget__description">
          Switch parcel lookup approach for testing
        </p>

        <RadioButtonGroup
          name="geoscape-approach"
          defaultSelected={selectedApproach}
          onChange={handleApproachChange}
          orientation="vertical"
        >
          <RadioButton
            id="approach-original"
            value={GeoscapeApproach.ORIGINAL}
            labelText="Original (findByIdentifier only)"
          />
          <RadioButton
            id="approach-replace"
            value={GeoscapeApproach.REPLACE}
            labelText="Replace (findByPoint only)"
          />
          <RadioButton
            id="approach-fallback"
            value={GeoscapeApproach.FALLBACK}
            labelText="Fallback (findByIdentifier + findByPoint)"
          />
        </RadioButtonGroup>

        <div className="geoscape-test-widget__info">
          <div className="geoscape-test-widget__info-item">
            <strong>Current:</strong> {selectedApproach}
          </div>
          <div className="geoscape-test-widget__info-item">
            <strong>Cost:</strong> {getApproachCost(selectedApproach)}
          </div>
        </div>
      </div>
    </div>
  );
};

const getApproachCost = (approach: GeoscapeApproach): string => {
  switch (approach) {
    case GeoscapeApproach.ORIGINAL:
      return '2 credit (findByIdentifier)';
    case GeoscapeApproach.REPLACE:
      return 'Up to 3 credits (address + findByPoint)';
    case GeoscapeApproach.FALLBACK:
      return 'Up to 5 credits (findByIdentifier + address + findByPoint)';
    default:
      return '1 credit';
  }
};

export default GeoscapeTestWidget;
